import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import Avatar from '@/components/common/Avatar';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import useRoutes from '@/hooks/useRoutes';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { ArrowLeft, Edit, ExternalLink, FileText, Calendar, User, ChevronDown, ChevronUp, Download, Paperclip } from 'lucide-react';

interface SubmissionData {
    id: number;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string | null;
    assignment: {
        id: number;
        title: string;
        course: { id: number; title: string };
        section: { id: number; title: string } | null;
        topic: { id: number; title: string } | null;
    };
    student: {
        id: number;
        full_name: string;
        email: string;
        avatar_url: string | null;
    };
    reviewed_by: {
        id: number;
        full_name: string;
    } | null;
    teacher_comment: string;
    url_1: string | null;
    url_2: string | null;
    evidence_url: string | null;
    evidence_filename: string | null;
    evidence_is_image: boolean;
    assignment_labels: {
        url_1_label: string;
        url_2_label: string;
        evidence_label: string;
    };
}

interface Assignment {
    id: number;
    title: string;
    description: string;
    points: number;
    url_1_label: string;
    url_2_label: string;
    evidence_label: string;
    attachments: {
        id: number;
        filename: string;
        file_url: string | null;
    }[];
}

interface SubmissionDetailProps {
    submission: SubmissionData;
    assignment: Assignment;
    submission_history: SubmissionData[];
}

const DetailContent: React.FC<SubmissionDetailProps> = ({
    submission,
    assignment,
    submission_history,
}) => {
    const { route } = useRoutes();
    const [isAssignmentDetailsOpen, setIsAssignmentDetailsOpen] = useState(false);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return (
                    <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800">
                        Aprobado
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                        No aprobado
                    </span>
                );
            case 'pending':
            default:
                return (
                    <span className="inline-flex rounded-full bg-yellow-100 px-3 py-1 text-sm font-semibold text-yellow-800">
                        Pendiente
                    </span>
                );
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-5xl">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link
                            href={route('staff-assignment-submissions').toString()}
                            className="rounded-md p-2 text-gray-600 dark:text-neutral-400 hover:bg-gray-100 dark:hover:bg-neutral-700"
                        >
                            <ArrowLeft size={20} />
                        </Link>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                Detalles de entrega
                            </h1>
                            <p className="mt-1 text-gray-600 dark:text-neutral-300">{assignment.title}</p>
                        </div>
                    </div>
                    <PrimaryButton
                        as="link"
                        href={route('staff-assignment-submissions-edit', {
                            submission_id: submission.id,
                        }).toString()}
                        size="sm"
                    >
                        <Edit size={16} />
                        Calificar
                    </PrimaryButton>
                </div>

                {/* Student Info Card */}
                <div className="mb-6 rounded-lg bg-white dark:bg-neutral-800 p-6 shadow">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                        Información del estudiante
                    </h2>
                    <div className="flex items-center gap-4">
                        <Avatar user={submission.student} size="lg" />
                        <div>
                            <div className="text-lg font-medium text-gray-900 dark:text-white">
                                {submission.student.full_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-neutral-400">
                                {submission.student.email}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submission Details Card */}
                <div className="mb-6 rounded-lg bg-white dark:bg-neutral-800 p-6 shadow">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Detalles de la entrega
                        </h2>
                        {getStatusBadge(submission.status)}
                    </div>

                    <div className="space-y-4">
                        {/* URL 1 */}
                        {submission.url_1 ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    {submission.assignment_labels.url_1_label}
                                </label>
                                <a
                                    href={submission.url_1}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                >
                                    {submission.url_1}
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        ) : null}

                        {/* URL 2 */}
                        {submission.url_2 ? (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    {submission.assignment_labels.url_2_label}
                                </label>
                                <a
                                    href={submission.url_2}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                >
                                    {submission.url_2}
                                    <ExternalLink size={16} />
                                </a>
                            </div>
                        ) : null}

                        {/* Evidence */}
                        {submission.evidence_url && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    {submission.assignment_labels.evidence_label}
                                </label>
                                {submission.evidence_is_image ? (
                                    <a
                                        href={submission.evidence_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            src={submission.evidence_url}
                                            alt={submission.evidence_filename || 'Evidencia'}
                                            className="mt-2 max-w-md rounded-lg border border-gray-200 dark:border-neutral-700"
                                        />
                                    </a>
                                ) : (
                                    <a
                                        href={submission.evidence_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download
                                        className="mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700 px-4 py-3 text-sm font-medium text-gray-700 dark:text-neutral-300 hover:border-emerald-400 hover:text-emerald-700"
                                    >
                                        <Paperclip size={16} />
                                        <span>{submission.evidence_filename || 'Descargar archivo'}</span>
                                        <Download size={16} />
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Dates */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    Fecha de entrega
                                </label>
                                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                    <Calendar size={16} />
                                    {formatDate(submission.created_at)}
                                </div>
                            </div>
                            {submission.reviewed_at && (
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                        Fecha de revisión
                                    </label>
                                    <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                        <Calendar size={16} />
                                        {formatDate(submission.reviewed_at)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Reviewed by */}
                        {submission.reviewed_by && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    Revisado por
                                </label>
                                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                    <User size={16} />
                                    {submission.reviewed_by.full_name}
                                </div>
                            </div>
                        )}

                        {/* Teacher Comment */}
                        {submission.teacher_comment && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    Comentarios del profesor
                                </label>
                                <div className="rounded-md bg-gray-50 dark:bg-neutral-700 p-3 text-sm text-gray-900 dark:text-white">
                                    {submission.teacher_comment}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Submission History Card */}
                <div className="mb-6 rounded-lg bg-white dark:bg-neutral-800 p-6 shadow">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                        Historial de entregas
                    </h2>

                    {submission_history.length === 0 ? (
                        <p className="text-sm text-gray-600 dark:text-neutral-300">
                            Esta es la primera entrega del estudiante para esta tarea.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                <thead className="bg-gray-50 dark:bg-neutral-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                            Fecha
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                            Estado
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                            Comentario
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">
                                            Revisado por
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
                                    {submission_history.map((sub) => (
                                        <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                {formatDate(sub.created_at)}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3">
                                                {getStatusBadge(sub.status)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                {sub.teacher_comment || '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                {sub.reviewed_by?.full_name || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Assignment Details Card (Collapsible) */}
                <div className="mb-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
                    <button
                        onClick={() => setIsAssignmentDetailsOpen(!isAssignmentDetailsOpen)}
                        className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-neutral-700"
                    >
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Detalles de la tarea
                        </h2>
                        {isAssignmentDetailsOpen ? (
                            <ChevronUp size={20} className="text-gray-500 dark:text-neutral-400" />
                        ) : (
                            <ChevronDown size={20} className="text-gray-500 dark:text-neutral-400" />
                        )}
                    </button>

                    {isAssignmentDetailsOpen && (
                        <div className="border-t border-gray-200 dark:border-neutral-700 p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                        Curso
                                    </label>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        {submission.assignment.course.title}
                                    </div>
                                </div>

                                {submission.assignment.section && (
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                            Sección
                                        </label>
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            {submission.assignment.section.title}
                                        </div>
                                    </div>
                                )}

                                {submission.assignment.topic && (
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                            Tema
                                        </label>
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            {submission.assignment.topic.title}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                        Puntos
                                    </label>
                                    <div className="text-sm text-gray-900 dark:text-white">{assignment.points}</div>
                                </div>

                                {assignment.description && (
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                            Descripción
                                        </label>
                                        <div
                                            className="html-content max-w-none text-sm text-gray-900 dark:text-white"
                                            dangerouslySetInnerHTML={{ __html: cleanWordPressHtml(assignment.description) }}
                                        />
                                    </div>
                                )}

                                {assignment.attachments.length > 0 && (
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                                            Archivos adjuntos
                                        </label>
                                        <div className="space-y-2">
                                            {assignment.attachments.map((attachment) => {
                                                const hasFileUrl = Boolean(attachment.file_url);

                                                if (hasFileUrl) {
                                                    return (
                                                        <a
                                                            key={attachment.id}
                                                            href={attachment.file_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                                                        >
                                                            <FileText size={16} />
                                                            {attachment.filename}
                                                            <ExternalLink size={14} />
                                                        </a>
                                                    );
                                                }

                                                return (
                                                    <div
                                                        key={attachment.id}
                                                        className="flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-400 cursor-not-allowed"
                                                        aria-disabled="true"
                                                    >
                                                        <FileText size={16} />
                                                        {attachment.filename}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </StaffLayout>
    );
};

const Detail: React.FC<SubmissionDetailProps> = (props) => {
    return (
        <ThemeWrapper>
            <DetailContent {...props} />
        </ThemeWrapper>
    );
};

export default Detail;
